import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { NavBack } from '../ui/NavBar.jsx'
import { buttonClasses } from '../ui/control.js'
import { logout } from '../auth.js'
import { Screen, ScreenTitle, PrimaryButton, ErrorNote } from '../ui/Screen.jsx'
import { Field, TextInput } from '../ui/Field.jsx'
import { useConfigBundle, consentOf } from '../config.js'
import { onboarding } from '../api.js'
import { rejectionCode } from '../answers.js'

/**
 * Step 1 of 3: consent. Then install, then notification permission.
 *
 * CONSENT TEXT IS NOT WRITTEN HERE AND MUST NEVER BE. It is an ethics document
 * and its wording is approved by whoever approves the study. Everything a
 * participant reads on this screen — the title, the sections, every affirmation
 * and the signature label — comes out of `bundle.consent`.
 *
 * The affirmations are RENDERED FROM THE ARRAY, not from three hardcoded boxes.
 * Adding a fourth affirmation later must be a config edit and nothing else,
 * exactly as with the item renderers.
 *
 * Consenting also generates and stores all 30 beep rows server-side, which is
 * why it is a real submit rather than a checkbox held locally.
 */
export default function Consent() {
  const navigate = useNavigate()
  const { data: bundle } = useConfigBundle()
  const consent = consentOf(bundle)

  const affirmations = Array.isArray(consent?.affirmations) ? consent.affirmations : []
  const signatureRules = consent?.signature ?? {}
  const needsName = signatureRules.requires_name !== false

  const [ticked, setTicked] = useState({})
  const [signature, setSignature] = useState('')
  const [failure, setFailure] = useState(null)
  const [busy, setBusy] = useState(false)

  // `blocking` is config's own switch and is authoritative — not "are there
  // sections". It goes false only once the text is the approved wording, and it
  // can go true again if wording is ever withdrawn.
  const textAvailable = consent?.blocking === false

  // Every affirmation marked required must be ticked, and the name typed.
  const requiredIds = affirmations.filter((a) => a.required).map((a) => a.id)
  const allAffirmed = requiredIds.every((id) => ticked[id])
  const nameGiven = !needsName || signature.trim().length > 1
  const ready = textAvailable && allAffirmed && nameGiven

  // A permanent state, not a transient failure. Retrying never helps, so the
  // screen must not offer it.
  const unavailable =
    !textAvailable || rejectionCode(failure) === 'consent_text_unavailable'

  /*
   * Declining signs her out and returns her to the start. It does NOT call an
   * endpoint: there is no decline route, and inventing a client-side "declined"
   * state would record a fact the server does not hold. Consent is recorded by
   * its presence, so the honest act is to leave without one.
   */
  async function decline() {
    await logout().catch(() => {})
    navigate('/login', { replace: true })
  }

  async function submit(e) {
    e.preventDefault()
    /*
     * Pressing while incomplete explains rather than doing nothing. Same rule
     * as the battery's Continue: the incomplete state is not entered until she
     * asks to leave, and asking is what pressing means.
     */
    if (!ready) {
      setFailure(
        new Error('Please tick each box and type your name before continuing.'),
      )
      return
    }
    setFailure(null)
    setBusy(true)
    try {
      await onboarding.consent({
        agreed: true,
        // Which affirmations were ticked, so backend can record them against
        // the version. Ids, never labels: the wording can be re-worded.
        affirmations: affirmations.filter((a) => ticked[a.id]).map((a) => a.id),
        signature: signature.trim(),
        // The APPROVED DOCUMENT's version, deliberately independent of
        // config_version, and stored on the participant row: we have to be able
        // to prove which wording each participant agreed to, and a config bump
        // for an unrelated reason must not read as a re-consent.
        consent_version: consent?.version ?? null,
      })
      /*
       * DS-5 §5.2 — the support screen is shown ONCE here, unprompted, before
       * it can ever be a verdict. Consent has just told her the app can show
       * support lines for her country; this is that promise demonstrated
       * rather than asserted, and it fixes the screen's meaning as "here is
       * what exists" before any answer can make it mean "this is about you".
       *
       * DS-6 §2.2 also moves demographics ahead of install and permission, so
       * this no longer goes to /install.
       */
      navigate('/support?onboarding=1', { replace: true })
    } catch (err) {
      setFailure(err)
    } finally {
      setBusy(false)
    }
  }

  return (
    <Screen
      nav={{ left: <NavBack />, right: 'Step 1 of 3' }}
      /*
       * AD-4 §4.3 — LIVE AND EXPLAINS, not disabled. `!ready` used to disable
       * this, which put a dead control at the bottom of the most important
       * screen in the study. It is `incomplete` now: pressing it does not
       * submit, it names what is still needed. `busy` is the one state that
       * genuinely disables, because the submit is already in flight.
       */
      action={
        <PrimaryButton type="submit" form="consent" incomplete={!ready} disabled={busy}>
          {busy ? 'Saving…' : 'Agree and continue'}
        </PrimaryButton>
      }
      /*
       * §5 — /consent GAINS A DECLINE, which /register already promises exists
       * ("you can decline it"). Until now the only way to decline was to close
       * the app, which is a refusal we never see and she never confirms.
       *
       * It is QUIET and in the footnote row, not a secondary beside the
       * primary. Declining is a real choice and must be reachable; making it
       * the visual equal of consenting would present a fork where the study
       * has a recommended path, and making it a hidden link would be the
       * banner pattern. Quiet-but-always-present is the same shape as every
       * other unconditional affordance in this app.
       */
      footnote={
        <button type="button" onClick={decline} className={buttonClasses('quiet')}>
          Decline
        </button>
      }
    >
      {/* `||` not `??`: config ships title as "" while the text is stubbed, and
          ?? passes an empty string straight through — an empty <h1>, which is
          an accessibility defect and reads as a broken page. */}
      <ScreenTitle>
        {consent?.title || 'Taking part in Editrics'}
      </ScreenTitle>

      {/*
        AD-3 §4.7 DocumentSection — consent is the ONE screen in this app that
        is genuinely read rather than scanned, so it is the one exception to the
        16px body.
        
        --t-document is 19px, which is GOV.UK's mobile body size: they set it
        there because low-confidence readers do measurably better with it, and
        this is the screen where that population matters most. Everything else
        in the app stays at 16px.
        
        THE ASYMMETRY IS WHAT MAKES IT A DOCUMENT. --s-8 above a heading and
        --s-3 below binds each heading to its own prose and separates it from
        the section before, so the eye gets an architecture instead of an even
        column. Equal spacing reads as a list of paragraphs; this reads as a
        document with parts.

        Each section is rendered from its BLOCKS, never re-split here — see
        blocksOf and ConsentBlock below, which is also where §4.7's Card is and
        why it is keyed on what config declares rather than on what it says.
        Guarded by qa/consent-document.mjs.
      */}
      {/*
        THE DOCUMENT IS ONE .t-prose CONTEXT. Every size, weight, margin and
        the callout inside come from the system in index.css - this file makes
        NO typographic decisions. The previous version made them per-element
        (and briefly per-indent), which is exactly how a document stops being
        a system.
      */}
      {textAvailable ? (
        <div className="t-prose mb-[var(--s-6)]">
          {(consent.sections ?? []).map((section, i) => (
            <section key={section.heading ?? i}>
              {section.heading ? <h2>{section.heading}</h2> : null}
              {blocksOf(section).map((block, j) => (
                <ConsentBlock key={block.id ?? j} block={block} />
              ))}
            </section>
          ))}
        </div>
      ) : null}

      {/* The honest version. Nothing failed to load and nothing is in flight:
          the approved document does not exist yet, so no amount of retrying
          will produce it. Saying "try again shortly" was a lie that also
          blamed the participant's connection. */}
      {unavailable ? (
        <div className="mb-6 rounded-[var(--r-surface)] border border-[var(--line)] bg-[var(--surface-sunken)] px-4 py-3">
          <p className="text-sm text-[var(--ink-900)]">
            Editrics isn&rsquo;t ready to take part in yet. The consent information
            hasn&rsquo;t been finalised, so nobody can sign up at the moment. Please check back
            another time - there&rsquo;s nothing you need to do.
          </p>
        </div>
      ) : null}

      {textAvailable ? (
        <form id="consent" onSubmit={submit} noValidate>
          {/* One checkbox per affirmation, from the array, in order. Never a
              fixed set: a fourth affirmation is a config edit and no code. */}
          <div className="mb-5 overflow-hidden rounded-[var(--r-surface)] border border-[var(--line)]">
          {affirmations.map((affirmation) => (
            /*
             * ONE LIST, NOT THREE FLOATING PANELS.
             *
             * Each affirmation was its own bordered --surface-raised box with a
             * 12px gap, so three related statements read as three unrelated
             * cards. They are one act - the thing she is agreeing to - and a
             * single grouped container with hairline rules says that, the same
             * shape used everywhere else in the app for a set of related rows.
             *
             * The ticked row tints rather than just showing a checked box, so
             * "how many have I done" is answerable by glancing at the block
             * instead of finding three small marks.
             */
            <label
              key={affirmation.id}
              className={
                'flex cursor-pointer items-start gap-3 border-b border-[var(--rule)] px-4 py-4 last:border-b-0 '
                + (ticked[affirmation.id] ? 'bg-[var(--accent-tint)]' : 'bg-[var(--surface-raised)]')
              }
            >
              <input
                type="checkbox"
                checked={Boolean(ticked[affirmation.id])}
                onChange={(e) =>
                  setTicked((prev) => ({ ...prev, [affirmation.id]: e.target.checked }))
                }
                /*
                 * min-h-0 KILLS THE GLOBAL 44px TAP FLOOR ON THIS INPUT, on
                 * the floor's own stated rule: it is "for controls a thumb
                 * actually lands on", and here the thumb lands on the ROW -
                 * the whole label is the target (~79px). Without this,
                 * Tailwind's h-5 sets height but not MIN-height, the global
                 * floor wins, the box renders 20x44, and the native glyph
                 * centres in it - painting ~13px below the text line. That was
                 * the droop in the operator's screenshot, invisible in the
                 * code because every class on this element looked correct.
                 */
                className="mt-0.5 h-5 w-5 min-h-0 shrink-0 accent-[var(--accent)]"
              />
              <span className="text-base leading-snug text-[var(--ink-900)]">{affirmation.text}</span>
            </label>
          ))}
          </div>

          {needsName ? (
            <Field
              id="signature"
              label={signatureRules.label || 'Type your full name to sign'}
              hint="This is your digital signature."
            >
              <TextInput
                id="signature"
                type="text"
                autoComplete="name"
                value={signature}
                onChange={(e) => setSignature(e.target.value)}
                hint
              />
            </Field>
          ) : null}

          {/* A permanent rejection is explained above, not repeated here as a
              retryable error. */}
          {rejectionCode(failure) === 'consent_text_unavailable' ? null : (
            <ErrorNote error={failure} />
          )}

        </form>
      ) : null}
    </Screen>
  )
}

/**
 * EVERY SECTION IS A LIST OF BLOCKS, INCLUDING THE ONES THAT DO NOT NEED TO BE.
 *
 * config put `blocks` on all three sections rather than only on the one the
 * Card needs, for a reason worth restating where it is consumed: a marker
 * present on some sections forces the renderer to ask "does this one have
 * blocks?" at every section, and that branch is the thing that eventually goes
 * wrong. So this normalises first and the renderer below has exactly one path.
 *
 * The one branch that survives is a VERSION branch, not a per-section one: a
 * bundle older than config 0.30.0 carries no blocks at all, and today's server
 * is still serving one (0.29.0). It renders `body` as a single paragraph and no
 * Card — which is precisely what this screen did yesterday. `blocks` is
 * additive and `body` stayed byte-identical, so that fallback is not a degraded
 * mode, it is the previous correct rendering.
 */
export function blocksOf(section) {
  const blocks = section?.blocks
  if (Array.isArray(blocks) && blocks.length) return blocks
  return [{ id: null, role: 'prose', text: section?.body ?? '' }]
}

/**
 * One paragraph of the consent document.
 *
 * KEYED ON `role`, WHICH IS THE DECLARED MEANING — never on the words and never
 * on the position. CFG-40 names both available markers ("the block with role
 * 'disclosure', id 'safety_disclosure'"), and the point they share is the one
 * that matters: identity comes from config, never from the prose. A renderer
 * that recognises this passage by its wording stops recognising it the day a
 * word legitimately changes, silently, on the consent screen — CR-14 finding 4
 * arriving in the worst possible place.
 *
 * Of the two I took `role`, on config's own stated principle for putting blocks
 * on every section rather than only the one that needs them: prefer the uniform
 * declared field to the special case. `role` is carried by every block; the id
 * is one paragraph's identity. So if the disclosure is ever re-worded and
 * re-keyed, `role` still finds it, and if a second is ever declared it is
 * treated the same way rather than silently left plain. The two markers then
 * work as two independent tripwires — config's validator constrains `role` to
 * `prose | disclosure`, and separately guards that the `safety_disclosure` id
 * exists — rather than as one marker checked twice.
 *
 * SPACING IS NOT DECIDED HERE. The document sits in a .t-prose context
 * (index.css) that owns the full type system: modular scale on the 19px body,
 * heading rhythm bound to what follows (1.6em above, 0.4em below), 0.75em
 * paragraph gaps, the callout object. This component renders semantic elements
 * - p, and .prose-callout for the disclosure - and makes no typographic
 * decisions. Two earlier schemes died here: per-element marginTop bookkeeping,
 * and a hanging indent that put body text on a second rail; both were local
 * decisions of exactly the kind a document system exists to forbid.
 */
function ConsentBlock({ block }) {
  const disclosure = block.role === 'disclosure'

  /*
   * `whitespace-pre-line` is kept even though every block config ships today is
   * a single line: dropping it would silently change how a future block with a
   * line break renders, and this is the one screen where "renders exactly what
   * config wrote" is the requirement.
   */
  if (!disclosure) {
    return (
      <p
        data-block-id={block.id ?? undefined}
        data-block-role="prose"
className="whitespace-pre-line"
      >
        {block.text}
      </p>
    )
  }

  /*
   * TWO CHANNELS, AS EVERYWHERE ELSE IN THIS APP. The Card carries the
   * container and full-weight ink carries the emphasis, because a single
   * channel is what DS-2 §1 rules out — and here the Card's fill is the channel
   * that vanishes in forced-colors, leaving the 1px border and the ink.
   *
   * --ink-900 rather than --ink-700 costs no contrast either way: --surface-raised
   * is LIGHTER than the paper it sits on, so both clear 4.5:1 inside the Card
   * more easily than outside it. Nothing here is louder than the words; the
   * passage is not dramatised, it is just not skimmable.
   */
  return (
    /*
     * DIMMED AND ANCHORED, NOT A WHITE BOX.
     *
     * This was the shared Card: --surface-raised (LIGHTER than the paper) with
     * a 1px --line border on all four sides. On a warm --surface page that
     * reads as a bright cut-out panel - the thing it is meant to do is slow
     * her down on the safety passage, and instead it just looked pasted on.
     *
     * Inverted: --surface-sunken recedes INTO the page rather than lifting off
     * it, and the emphasis comes from a 3px --accent bar on the leading edge.
     * A bar is a stronger structural signal than a rectangle and costs no
     * contrast, and the fill can now be quieter because the bar is carrying
     * the weight.
     *
     * STILL TWO CHANNELS, per DS-2 §1: the leading bar plus --ink-900 at full
     * weight. The bar is the one that survives forced-colors, where fills are
     * discarded and borders are not - the same reason the old Card kept its
     * 1px border.
     */
    <div
      data-block-id={block.id ?? undefined}
      data-block-role="disclosure"
className="prose-callout"
    >
      <p className="whitespace-pre-line">{block.text}</p>
    </div>
  )
}
